import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, TrendingUp, TrendingDown, Plus, Save } from "lucide-react";

const HeadRows = ({ heads, amt, setAmt, testid }) => (
  <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
    {heads.map((h) => (
      <div key={h} className="flex items-center gap-3">
        <span className="flex-1 text-sm text-slate-300 truncate">{h}</span>
        <div className="relative w-36">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">₹</span>
          <Input
            data-testid={`${testid}-${h.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
            type="number"
            value={amt[h] ?? ""}
            onChange={(e) => setAmt((p) => ({ ...p, [h]: e.target.value }))}
            className="bg-[#0F172A] border-[#334155] h-9 pl-6 tabular-nums text-right focus:border-[#C9A24B]"
            placeholder="0"
          />
        </div>
      </div>
    ))}
  </div>
);

export default function CenterPnL() {
  const { period, monthLabel } = useOutletContext();
  const [settings, setSettings] = useState(null);
  const [center, setCenter] = useState("");
  const [incomeAmt, setIncomeAmt] = useState({});
  const [expenseAmt, setExpenseAmt] = useState({});
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newIncome, setNewIncome] = useState("");
  const [newExpense, setNewExpense] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      const centers = r.data.centers || [];
      setCenter((c) => c || centers[0] || "");
    });
  }, []);

  const loadSummary = useCallback(() => {
    api.get(`/center-summary?period=${period}`).then((r) => setSummary(r.data)).catch(() => {});
  }, [period]);

  const loadEntries = useCallback(() => {
    if (!center || !period) return;
    api.get(`/center-entries?month=${period}&center=${encodeURIComponent(center)}`).then((r) => {
      const inc = {}, exp = {};
      r.data.forEach((e) => { if (e.kind === "income") inc[e.head] = e.amount; else exp[e.head] = e.amount; });
      setIncomeAmt(inc); setExpenseAmt(exp);
    });
  }, [center, period]);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const incomeTotal = Object.values(incomeAmt).reduce((a, b) => a + Number(b || 0), 0);
  const expenseTotal = Object.values(expenseAmt).reduce((a, b) => a + Number(b || 0), 0);
  const net = incomeTotal - expenseTotal;

  const save = async () => {
    if (!center) { toast.error("Select a centre"); return; }
    const entries = [];
    (settings.income_heads || []).forEach((h) => entries.push({ kind: "income", head: h, amount: Number(incomeAmt[h] || 0) }));
    (settings.expense_heads || []).forEach((h) => entries.push({ kind: "expense", head: h, amount: Number(expenseAmt[h] || 0) }));
    setSaving(true);
    try {
      await api.post("/center-entries/bulk", { center, month: period, entries });
      toast.success(`Saved ${center} P&L for ${monthLabel(period)}`);
      loadSummary();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addHead = async (kind) => {
    const val = kind === "income" ? newIncome : newExpense;
    if (!val.trim()) return;
    const key = kind === "income" ? "income_heads" : "expense_heads";
    const updated = { ...settings, [key]: [...(settings[key] || []), val.trim()] };
    try {
      await api.put("/settings", updated);
      setSettings(updated);
      if (kind === "income") setNewIncome(""); else setNewExpense("");
      toast.success("Head added");
    } catch (e) { toast.error("Could not add head"); }
  };

  if (!settings) return <div className="py-32 text-center text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Centre P&amp;L</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">Fill centre-wise income &amp; expense against each head for the selected month. Totals roll up to the Dashboard as the <span className="text-[#C9A24B]">Awish</span> total.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={center} onValueChange={setCenter}>
            <SelectTrigger data-testid="center-selector" className="w-[180px] bg-[#0F172A] border-[#334155] h-10">
              <SelectValue placeholder="Select centre" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-[#334155] text-slate-100">
              {(settings.centers || []).map((c) => <SelectItem key={c} value={c} className="focus:bg-[#334155]">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="center-save" onClick={save} disabled={saving} className="bg-[#C9A24B] hover:bg-[#D4AF37] text-[#0B1120] font-semibold">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save P&L"}
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-xl border border-emerald-600/30 bg-[#1E293B] p-5">
          <div className="flex items-center gap-2 text-emerald-400 mb-1"><TrendingUp className="h-4 w-4" /><span className="label-caps">Revenue</span></div>
          <div className="font-heading text-2xl font-bold tabular-nums">{inr(incomeTotal)}</div>
        </div>
        <div className="rounded-xl border border-rose-600/30 bg-[#1E293B] p-5">
          <div className="flex items-center gap-2 text-rose-400 mb-1"><TrendingDown className="h-4 w-4" /><span className="label-caps">Expense</span></div>
          <div className="font-heading text-2xl font-bold tabular-nums">{inr(expenseTotal)}</div>
        </div>
        <div className="rounded-xl border border-[#C9A24B]/40 bg-[#1E293B] p-5">
          <div className="label-caps mb-1">Net ({monthLabel(period)})</div>
          <div className={`font-heading text-2xl font-bold tabular-nums ${net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{inr(net)}</div>
        </div>
      </div>

      {/* Income & Expense grids */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-[#334155] bg-[#1E293B] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-medium flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-400" /> Income Classifications</h3>
            <span className="text-sm text-emerald-400 tabular-nums">{inr(incomeTotal)}</span>
          </div>
          <HeadRows heads={settings.income_heads || []} amt={incomeAmt} setAmt={setIncomeAmt} testid="income-input" />
          <div className="flex gap-2 mt-4 pt-4 border-t border-[#334155]">
            <Input data-testid="add-income-head-input" value={newIncome} onChange={(e) => setNewIncome(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addHead("income")} placeholder="Add income head…" className="bg-[#0F172A] border-[#334155] h-9" />
            <Button data-testid="add-income-head" onClick={() => addHead("income")} className="bg-[#334155] hover:bg-[#475569] shrink-0 h-9"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="rounded-xl border border-[#334155] bg-[#1E293B] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-medium flex items-center gap-2"><TrendingDown className="h-4 w-4 text-rose-400" /> Expenditure Heads</h3>
            <span className="text-sm text-rose-400 tabular-nums">{inr(expenseTotal)}</span>
          </div>
          <HeadRows heads={settings.expense_heads || []} amt={expenseAmt} setAmt={setExpenseAmt} testid="expense-input" />
          <div className="flex gap-2 mt-4 pt-4 border-t border-[#334155]">
            <Input data-testid="add-expense-head-input" value={newExpense} onChange={(e) => setNewExpense(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addHead("expense")} placeholder="Add expense head…" className="bg-[#0F172A] border-[#334155] h-9" />
            <Button data-testid="add-expense-head" onClick={() => addHead("expense")} className="bg-[#334155] hover:bg-[#475569] shrink-0 h-9"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* All centres comparison */}
      {summary && (
        <div className="rounded-xl border border-[#334155] bg-[#1E293B] overflow-hidden" data-testid="center-comparison">
          <div className="px-6 py-4 border-b border-[#334155] flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#C9A24B]" />
            <h3 className="font-heading font-medium">All Centres — {monthLabel(period)}</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#334155] bg-[#0F172A]/40">
                <th className="text-left label-caps px-6 py-3">Centre</th>
                <th className="text-right label-caps px-6 py-3">Revenue</th>
                <th className="text-right label-caps px-6 py-3">Expense</th>
                <th className="text-right label-caps px-6 py-3">Net</th>
              </tr>
            </thead>
            <tbody>
              {summary.centers.map((c) => (
                <tr key={c.center} className="border-b border-[#334155]/50">
                  <td className="px-6 py-3 text-slate-200">{c.center}</td>
                  <td className="px-6 py-3 text-right tabular-nums text-emerald-400">{inr(c.income)}</td>
                  <td className="px-6 py-3 text-right tabular-nums text-rose-400">{inr(c.expense)}</td>
                  <td className={`px-6 py-3 text-right tabular-nums font-medium ${c.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{inr(c.net)}</td>
                </tr>
              ))}
              <tr className="bg-[#C9A24B]/8 font-semibold">
                <td className="px-6 py-3.5 text-[#C9A24B]">Awish — Total</td>
                <td className="px-6 py-3.5 text-right tabular-nums">{inr(summary.total_income)}</td>
                <td className="px-6 py-3.5 text-right tabular-nums">{inr(summary.total_expense)}</td>
                <td className={`px-6 py-3.5 text-right tabular-nums ${summary.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{inr(summary.net)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
